import React, { useEffect, useState } from 'react';
import ActionButton from '../components/ActionButton';
import DataStatusBanner from '../components/DataStatusBanner';
import { useCart } from '../context/CartContext';
import useRemoteData from '../hooks/useRemoteData';
import { fetchCombos } from '../lib/api';

const CombosPage = () => {
  const [quantities, setQuantities] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const { addItem } = useCart();
  const { data: combos, loading, error } = useRemoteData(fetchCombos, [], { cacheKey: 'combos' });

  const getQuantity = (comboId) => quantities[comboId] || 1;

  const setQuantity = (comboId, qty) => {
    setQuantities((prev) => ({ ...prev, [comboId]: Math.max(1, qty) }));
  };

  const handleAddToCart = (combo) => {
    const qty = getQuantity(combo.id);
    addItem(
      { id: combo.id, name: combo.name, price: combo.price, type: 'combo', image: combo.image || null },
      qty
    );
    setConfirmation(`${qty}× ${combo.name} added to cart!`);
    setQuantity(combo.id, 1);
  };

  useEffect(() => {
    if (!confirmation) return undefined;
    const timeout = setTimeout(() => setConfirmation(null), 2800);
    return () => clearTimeout(timeout);
  }, [confirmation]);

  return (
    <main className="combos-page">
      {/* Hero Section */}
      <section className="combos-hero">
        <div className="combos-hero-content">
          <p className="eyebrow">Curated packages</p>
          <h1>Complete meals for every occasion</h1>
          <p>
            Ready-made combos with signature mains, sides, and proteins. Perfect for gatherings, 
            events, or feeding a crowd. Mix, match, or customize.
          </p>
        </div>
      </section>

     

      {/* Combos Grid */}
      <section className="combos-grid-section">
        <div className="combos-intro">
          <h2>Our combo packages</h2>
          <p>Select quantity and add to your order</p>
        </div>

        <DataStatusBanner
          loading={loading}
          error={error}
          empty={!loading && !error && combos.length === 0}
          emptyMessage="Combo packages are unavailable right now. Please check back shortly."
        />

        {!loading && !error && combos.length > 0 && (
        <div className="combos-grid">
          {combos.map((combo) => (
            <article key={combo.id} className="combo-card">
              <div className="combo-image-wrapper">
                {combo.image ? (
                  <img src={combo.image} alt={combo.name} loading="lazy" />
                ) : (
                  <div className="combo-image-placeholder">No image</div>
                )}
                {combo.savingsText && (
                  <div className="combo-badge">
                    <span>{combo.savingsText}</span>
                  </div>
                )}
              </div>

              <div className="combo-info">
                <h3>{combo.name}</h3>
                <p className="combo-description">{combo.description}</p>

                <div className="combo-items">
                  <p className="items-label">Includes:</p>
                  <ul>
                    {(combo.items || []).map((item) => (
                      <li key={item}>
                        <span className="item-icon">🍽️</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="combo-footer">
                  <div className="combo-price-info">
                    <span className="combo-price">₦{combo.price.toLocaleString()}</span>
                    <span className="combo-savings">{combo.savingsText}</span>
                  </div>
                </div>

                <div className="combo-card-quantity">
                  <label htmlFor={`qty-${combo.id}`}>Qty:</label>
                  <div className="quantity-controls">
                    <button
                      onClick={() => setQuantity(combo.id, getQuantity(combo.id) - 1)}
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    <input
                      id={`qty-${combo.id}`}
                      type="number"
                      min="1"
                      value={getQuantity(combo.id)}
                      onChange={(e) => setQuantity(combo.id, parseInt(e.target.value) || 1)}
                    />
                    <button
                      onClick={() => setQuantity(combo.id, getQuantity(combo.id) + 1)}
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                <ActionButton
                  variant="primary"
                  size="sm"
                  onClick={() => handleAddToCart(combo)}
                  className="combo-add-btn"
                >
                  Add to order
                </ActionButton>

                {confirmation && confirmation.includes(combo.name) && (
                  <p className="combo-added-feedback">{confirmation}</p>
                )}
              </div>
            </article>
          ))}
        </div>
        )}
      </section>

      {/* FAQ Section */}
      <section className="combos-faq">
        <h2>About our combos</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h3>Can I customize a combo?</h3>
            <p>Absolutely. Swap dishes, change portion sizes, or adjust spice levels. Contact us to discuss custom options for your event.</p>
          </div>
          <div className="faq-item">
            <h3>How far in advance should I order?</h3>
            <p>Standard combos: 2–3 days. Custom combos: 1–2 weeks for large events. Call us to check availability and book.</p>
          </div>
          <div className="faq-item">
            <h3>Do you offer delivery?</h3>
            <p>Yes. Delivery is available across Asaba, Delta State for orders placed by noon the day before. Rush delivery available on request.</p>
          </div>
          <div className="faq-item">
            <h3>Are dietary restrictions supported?</h3>
            <p>Yes. Vegan, vegetarian, gluten-free, and allergy-conscious options available. Let us know when ordering.</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="combos-cta">
        <h2>Ready to order?</h2>
        <p>Select a combo above or contact us to discuss a custom package for your event.</p>
        <div className="combos-cta-buttons">
          <ActionButton to="/contact" variant="primary" size="sm">
            Place an order
          </ActionButton>
          <ActionButton to="/menu" variant="outline" size="sm">
            Browse full menu
          </ActionButton>
          <ActionButton to="/services" variant="ghost" size="sm">
            Explore catering services
          </ActionButton>
        </div>
      </section>
    </main>
  );
};

export default CombosPage;
